import { useState, useCallback, useEffect, useRef } from "react";
import jmespath from "jmespath";
import jsonata from "jsonata";
import { containsOnlyAllowedJsonataFunctions, JsonValue } from "../../../../utils/expressionUtils";

type useEditJsonAdvancedMappingExpressionModalParams = {
  currentJsonAdvancedMappingExpression: string | null;
  jsonData: JsonValue;
  isJmesPathDeprecationEnabled?: boolean;
};
const useEditJsonAdvancedMappingExpressionModal = ({
  currentJsonAdvancedMappingExpression,
  jsonData,
  isJmesPathDeprecationEnabled = false,
}: useEditJsonAdvancedMappingExpressionModalParams) => {
  // state
  const [jsonAdvancedMappingExpression, setJsonAdvancedMappingExpression] = useState<string | null>(
    currentJsonAdvancedMappingExpression,
  );
  const [jsonAdvancedMappingExpressionResult, setJsonAdvancedMappingExpressionResult] =
    useState<string>("");
  const [inputError, setInputError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // event handlers
  const handleJsonAdvancedMappingExpressionInput = useCallback(
    async (expression: string | null) => {
      if (expression == null || expression == "") {
        setJsonAdvancedMappingExpression("");
        setInputError(false);
        setErrorMessage(null);
        setJsonAdvancedMappingExpressionResult("");
        return;
      }
      try {
        setJsonAdvancedMappingExpression(expression);
        let result: string;
        if (isJmesPathDeprecationEnabled) {
          // Check for disallowed functions
          if (!containsOnlyAllowedJsonataFunctions(expression)) {
            setInputError(true);
            setErrorMessage("Invalid expression - please verify the expression");
            setJsonAdvancedMappingExpressionResult("");
            return;
          }
          const jsonataExpression = jsonata(expression);
          result = JSON.stringify(await jsonataExpression.evaluate(jsonData));
        } else {
          result = JSON.stringify(jmespath.search(jsonData, expression));
        }
        setInputError(false);
        setErrorMessage(null);
        setJsonAdvancedMappingExpressionResult(result);
      } catch (e) {
        setInputError(true);
        setErrorMessage((e as { message?: string })?.message ?? "Invalid expression");
        setJsonAdvancedMappingExpressionResult("");
      }
    },
    [jsonData, isJmesPathDeprecationEnabled],
  );

  // The mapping's saved expression is the starting point for an edit, so the result preview is
  // filled in from it when the modal opens.
  const hasEvaluatedSavedExpression = useRef(false);
  useEffect(() => {
    if (hasEvaluatedSavedExpression.current) {
      return;
    }
    hasEvaluatedSavedExpression.current = true;
    if (currentJsonAdvancedMappingExpression) {
      handleJsonAdvancedMappingExpressionInput(currentJsonAdvancedMappingExpression);
    }
  }, [currentJsonAdvancedMappingExpression, handleJsonAdvancedMappingExpressionInput]);

  return {
    jsonAdvancedMappingExpression,
    jsonAdvancedMappingExpressionResult,
    inputError,
    errorMessage,
    handleJsonAdvancedMappingExpressionInput,
  };
};

export default useEditJsonAdvancedMappingExpressionModal;
